import React, { useEffect, useMemo, useRef } from "react"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

/**
 * Card-type distribution within each payment method (grouped bars).
 *
 * Reads the nested `data.payment_gateway_usage.by_method`:
 *   {
 *     CARD:     { total, by_card_type: { VISA, MASTER, MADA, UNKNOWN } },
 *     APPLEPAY: { total, by_card_type: { ... } },
 *     TAMARA:   { total, by_card_type: { ... } }
 *   }
 *
 * Each method shows one bar per card type it actually has, packed together and
 * centered under the method name. Card types are discovered dynamically, so a
 * new brand from the backend (e.g. AMEX) shows up automatically. Tamara has no
 * card breakdown, so it renders as a single "Tamara" bar.
 */

const METHOD_LABELS = {
  CARD: { ar: "بطاقة", en: "Card" },
  APPLEPAY: { ar: "أبل باي", en: "Apple Pay" },
  TAMARA: { ar: "تمارا", en: "Tamara" },
}

// Fixed method order on the axis: Card, Apple Pay, Tamara.
const METHOD_ORDER = ["CARD", "APPLEPAY", "TAMARA"]

// Preferred card-type order + colors. TAMARA is a BNPL method with no card
// breakdown — its transactions show as a single "Tamara" bar.
const CARD_ORDER = ["VISA", "MASTER", "MADA", "TAMARA"]
// Card types we don't surface (unidentified card brand).
const EXCLUDED_CARD_TYPES = new Set(["UNKNOWN"])
const CARD_LABELS = {
  VISA: { ar: "فيزا", en: "Visa" },
  MASTER: { ar: "ماستركارد", en: "Mastercard" },
  MADA: { ar: "مدى", en: "mada" },
  TAMARA: { ar: "تمارا", en: "Tamara" },
}
const CARD_COLORS = {
  VISA: "#7283d3",
  MASTER: "#c17444",
  MADA: "#59cfa9",
  TAMARA: "#7146bc",
}
// Fallback colors for card types we didn't anticipate.
const PALETTE = ["#522a8e", "#353e6c", "#59cf7a", "#c16544", "#EC4899"]

const CardTypeByMethodChart = ({ data }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  const lang =
    typeof window !== "undefined"
      ? localStorage.getItem("language") || "ar"
      : "ar"
  const isAr = lang === "ar"
  const L = (map, key) => map[key]?.[isAr ? "ar" : "en"] || key

  // Build the flat, packed bar model once per data/lang change.
  const model = useMemo(() => {
    const byMethod = data?.payment_gateway_usage?.by_method || {}
    const methodKeys = [
      ...METHOD_ORDER.filter((m) => byMethod[m]),
      ...Object.keys(byMethod).filter((m) => !METHOD_ORDER.includes(m)),
    ]

    // Per-method card-type counts. Tamara has no real card breakdown, so its
    // whole total is a single "Tamara" bar (never "Unknown").
    const cardCountsByMethod = {}
    methodKeys.forEach((m) => {
      cardCountsByMethod[m] =
        m === "TAMARA"
          ? { TAMARA: byMethod[m]?.total || 0 }
          : { ...(byMethod[m]?.by_card_type || {}) }
    })

    // Card types actually present (minus excluded), preferred order first.
    const present = new Set()
    methodKeys.forEach((m) => {
      Object.keys(cardCountsByMethod[m]).forEach((c) => {
        if (!EXCLUDED_CARD_TYPES.has(c) && (cardCountsByMethod[m][c] || 0) > 0)
          present.add(c)
      })
    })
    const cardKeys = [
      ...CARD_ORDER.filter((c) => present.has(c)),
      ...[...present].filter((c) => !CARD_ORDER.includes(c)),
    ]
    const colorFor = (c) =>
      CARD_COLORS[c] || PALETTE[Math.max(0, cardKeys.indexOf(c)) % PALETTE.length]

    // Flat bar list with a spacer between method groups; track each method's
    // bar index span so its label can be centered under its bars.
    const bars = []
    const methodSpans = []
    // Sum of the card values actually shown per method (excludes UNKNOWN), so
    // tooltip percentages within a method add up to 100%.
    const byMethodVisibleTotal = {}
    methodKeys.forEach((m, mi) => {
      const cardsInM = cardKeys.filter((c) => (cardCountsByMethod[m][c] || 0) > 0)
      byMethodVisibleTotal[m] = cardsInM.reduce(
        (s, c) => s + (cardCountsByMethod[m][c] || 0),
        0
      )
      if (mi > 0) bars.push({ spacer: true })
      const start = bars.length
      cardsInM.forEach((c) =>
        bars.push({
          value: cardCountsByMethod[m][c],
          color: colorFor(c),
          method: m,
          card: c,
        })
      )
      methodSpans.push({
        method: m,
        label: L(METHOD_LABELS, m),
        start,
        end: bars.length - 1,
        hasBars: cardsInM.length > 0,
      })
    })

    return {
      cardKeys,
      colorFor,
      bars,
      methodSpans,
      byMethodVisibleTotal,
      isNoData: bars.filter((b) => !b.spacer).length === 0,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAr])

  useEffect(() => {
    if (!chartRef.current) return
    if (chartInstance.current) chartInstance.current.destroy()

    const { bars, methodSpans, byMethodVisibleTotal, isNoData } = model
    const noDataText = isAr ? "لا توجد بيانات" : "No Data"

    // Draw the value above each bar.
    const barValuePlugin = {
      id: "cardBarValues",
      afterDatasetsDraw(chart) {
        const { ctx } = chart
        const meta = chart.getDatasetMeta(0)
        ctx.save()
        ctx.font = "600 10px Arial"
        ctx.fillStyle = "#475467"
        ctx.textAlign = "center"
        ctx.textBaseline = "bottom"
        meta.data.forEach((bar, i) => {
          const val = chart.data.datasets[0].data[i]
          if (!val) return
          ctx.fillText(String(val), bar.x, bar.y - 3)
        })
        ctx.restore()
      },
    }

    // Draw each method label centered under its own bars.
    const methodLabelPlugin = {
      id: "cardMethodLabels",
      afterDraw(chart) {
        if (isNoData) return
        const { ctx, chartArea } = chart
        const meta = chart.getDatasetMeta(0)
        ctx.save()
        ctx.font = "500 12px Arial"
        ctx.fillStyle = "#475467"
        ctx.textAlign = "center"
        ctx.textBaseline = "top"
        methodSpans.forEach((span) => {
          if (!span.hasBars) return
          const xs = []
          for (let i = span.start; i <= span.end; i++) {
            const bar = meta.data[i]
            if (bar) xs.push(bar.x)
          }
          if (!xs.length) return
          const center = (Math.min(...xs) + Math.max(...xs)) / 2
          ctx.fillText(span.label, center, chartArea.bottom + 10)
        })
        ctx.restore()
      },
    }

    const centerNoData = {
      id: "cardNoData",
      afterDraw(chart) {
        if (!isNoData) return
        const { ctx, chartArea } = chart
        ctx.save()
        ctx.fillStyle = "#6B7280"
        ctx.font = "bold 15px Arial"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(
          noDataText,
          (chartArea.left + chartArea.right) / 2,
          (chartArea.top + chartArea.bottom) / 2
        )
        ctx.restore()
      },
    }

    const ctx = chartRef.current.getContext("2d")
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: bars.map((_, i) => i),
        datasets: [
          {
            data: bars.map((b) => (b.spacer ? null : b.value)),
            backgroundColor: bars.map((b) => b.color || "transparent"),
            borderWidth: 0,
            borderRadius: 4,
            maxBarThickness: 46,
            categoryPercentage: 0.9,
            barPercentage: 0.86,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 18, bottom: 22 } },
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { display: false },
            border: { display: false },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#F2F4F7", drawBorder: false },
            border: { display: false },
            ticks: { color: "#98A2B3", font: { size: 11 } },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            filter: (item) => !bars[item.dataIndex]?.spacer,
            callbacks: {
              title: (items) => {
                const b = bars[items[0].dataIndex]
                return b ? L(METHOD_LABELS, b.method) : ""
              },
              label: (c) => {
                const b = bars[c.dataIndex]
                if (!b) return ""
                // Percentage out of the method's visible cards (excl. Unknown),
                // so the shown bars sum to 100%.
                const total = byMethodVisibleTotal[b.method] || 0
                const pct = total ? ((b.value / total) * 100).toFixed(1) : "0.0"
                return `${L(CARD_LABELS, b.card)}: ${b.value} (${pct}%)`
              },
            },
          },
        },
      },
      plugins: [barValuePlugin, methodLabelPlugin, centerNoData],
    })

    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, lang])

  return (
    <div className="card-method-chart">
      <div className="card-method-canvas">
        <canvas ref={chartRef} />
      </div>
      {model.cardKeys.length > 0 && (
        <ul className="card-method-legend">
          {model.cardKeys.map((c) => (
            <li key={c}>
              <span
                className="card-method-legend-dot"
                style={{ background: model.colorFor(c) }}
              />
              {L(CARD_LABELS, c)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CardTypeByMethodChart
